import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ChartBarIcon, CalculatorIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analysis = () => {
  const [analysisData, setAnalysisData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchProduct, setSearchProduct] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalChannels: 0,
    avgMolPercentage: 0,
    bestChannel: null
  });

  useEffect(() => {
    loadAnalysisData();
  }, []);

  const loadAnalysisData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analysis/margins`);
      setAnalysisData(response.data);
      
      // Calcola statistiche
      if (response.data.length > 0) {
        const uniqueProducts = [...new Set(response.data.map(item => item.product_id))];
        const uniqueChannels = [...new Set(response.data.map(item => item.channel_id))];
        const avgMol = response.data.reduce((sum, item) => sum + item.mol_percentage, 0) / response.data.length;
        
        // Trova il canale con MOL medio più alto
        const channelMols = {};
        response.data.forEach(item => {
          if (!channelMols[item.channel_name]) {
            channelMols[item.channel_name] = [];
          }
          channelMols[item.channel_name].push(item.mol_percentage);
        });
        
        let bestChannel = null;
        let highestAvgMol = -Infinity;
        Object.entries(channelMols).forEach(([channelName, mols]) => {
          const avg = mols.reduce((sum, mol) => sum + mol, 0) / mols.length;
          if (avg > highestAvgMol) {
            highestAvgMol = avg;
            bestChannel = channelName;
          }
        });
        
        setStats({
          totalProducts: uniqueProducts.length,
          totalChannels: uniqueChannels.length,
          avgMolPercentage: avgMol,
          bestChannel: bestChannel
        });
      }
    } catch (error) {
      console.error('Errore nel caricamento dell\'analisi:', error);
      toast.error('Errore nel caricamento dell\'analisi margini');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  // Ottieni elenco canali unici
  const uniqueChannels = useMemo(() => {
    const channels = [...new Set(analysisData.map(item => item.channel_name))];
    return channels.sort();
  }, [analysisData]);

  // Filtra i dati in base a ricerca prodotto e canale selezionato
  const filteredData = useMemo(() => {
    return analysisData.filter(item => {
      const matchesProduct = searchProduct === '' || 
        item.product_name.toLowerCase().includes(searchProduct.toLowerCase()) ||
        item.product_code.toLowerCase().includes(searchProduct.toLowerCase());
      
      const matchesChannel = filterChannel === '' || item.channel_name === filterChannel;
      
      return matchesProduct && matchesChannel;
    });
  }, [analysisData, searchProduct, filterChannel]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analysis-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analisi Margini</h1>
          <p className="text-gray-600">Analisi MOL (Margine Operativo Lordo) per prodotto e canale</p>
        </div>
        <Button data-testid="generate-analysis-btn">
          <CalculatorIcon className="w-4 h-4 mr-2" />
          Genera Analisi
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ChartBarIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Prodotti Analizzati</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Canali Attivi</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalChannels}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CalculatorIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">MOL Medio</p>
                <p className="text-2xl font-bold text-gray-900">{formatPercentage(stats.avgMolPercentage)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ArrowTrendingUpIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Miglior Canale</p>
                <p className="text-sm font-bold text-gray-900">{stats.bestChannel || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analysis Results */}
      <Card>
        <CardHeader>
          <CardTitle>Analisi Dettagliata Margini</CardTitle>
          <CardDescription>
            Margini operativi lordi calcolati per ogni combinazione prodotto/canale
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filtri di Ricerca */}
          {analysisData.length > 0 && (
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              {/* Ricerca Prodotto */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MagnifyingGlassIcon className="w-4 h-4 inline mr-1" />
                  Cerca Prodotto
                </label>
                <input
                  type="text"
                  placeholder="Nome o codice prodotto..."
                  value={searchProduct}
                  onChange={(e) => setSearchProduct(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="search-product-input"
                />
              </div>

              {/* Filtro Canale */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FunnelIcon className="w-4 h-4 inline mr-1" />
                  Filtra per Canale
                </label>
                <select
                  value={filterChannel}
                  onChange={(e) => setFilterChannel(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="filter-channel-select"
                >
                  <option value="">Tutti i Canali</option>
                  {uniqueChannels.map(channel => (
                    <option key={channel} value={channel}>{channel}</option>
                  ))}
                </select>
              </div>

              {/* Conteggio Risultati e Reset */}
              <div className="flex items-end">
                <div className="flex flex-col gap-2">
                  <div className="text-sm text-gray-600">
                    Mostrando <strong>{filteredData.length}</strong> di <strong>{analysisData.length}</strong> risultati
                  </div>
                  {(searchProduct || filterChannel) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchProduct('');
                        setFilterChannel('');
                      }}
                      className="text-xs"
                    >
                      Reset Filtri
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {analysisData.length === 0 ? (
            <div className="text-center py-16">
              <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna analisi disponibile</h3>
              <div className="space-y-4 text-sm text-gray-600 max-w-2xl mx-auto">
                <p>
                  Per generare l'analisi dei margini, assicurati di aver configurato:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg text-left">
                  <ul className="space-y-2">
                    <li>• <strong>Ingredienti:</strong> con costi per unità e percentuali alcol</li>
                    <li>• <strong>Ricette:</strong> che definiscono la composizione del liquido</li>
                    <li>• <strong>Componenti Packaging:</strong> con costi per unità</li>
                    <li>• <strong>Prodotti:</strong> con listini commerciali e pubblici</li>
                    <li>• <strong>Canali di Vendita:</strong> con sconti e costi specifici</li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg text-left">
                  <h4 className="font-medium text-blue-900 mb-2">Calcoli automatici inclusi:</h4>
                  <ul className="space-y-1 text-blue-800 text-xs">
                    <li>• <strong>Costo Industriale:</strong> Ricetta + Packaging</li>
                    <li>• <strong>Accise:</strong> Volume alcol anidro × 10,3552€ × 1,22 (IVA)</li>
                    <li>• <strong>Contrassegno di Stato:</strong> 0,047€ per bottiglia</li>
                    <li>• <strong>Prezzo Finale:</strong> Dopo sconti canale</li>
                    <li>• <strong>MOL:</strong> (Prezzo Finale - Costi Totali) / Prezzo Finale × 100</li>
                  </ul>
                </div>
              </div>
              
              <Button className="mt-6" onClick={loadAnalysisData}>
                <CalculatorIcon className="w-4 h-4 mr-2" />
                Aggiorna Analisi
              </Button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-16">
              <MagnifyingGlassIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun risultato trovato</h3>
              <p className="text-sm text-gray-600 mb-4">
                Prova a modificare i filtri di ricerca
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchProduct('');
                  setFilterChannel('');
                }}
              >
                Reset Filtri
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-900">Prodotto</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-900">Canale</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-900">Prezzo Base<br/><span className="text-xs font-normal text-gray-500">(senza accisa)</span></th>
                    <th className="px-4 py-3 text-right font-medium text-gray-900">Con Accisa<br/><span className="text-xs font-normal text-gray-500">(dopo sconti)</span></th>
                    <th className="px-4 py-3 text-right font-medium text-gray-900">Costi Add.</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-900">Prezzo Netto<br/><span className="text-xs font-normal text-green-600">Canale</span></th>
                    <th className="px-4 py-3 text-right font-medium text-gray-900">Costo Ind.</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-900">MOL €</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-900">MOL %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50" data-testid={`analysis-row-${index}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.product_name}</div>
                        <div className="text-xs text-gray-500">{item.product_code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.channel_name}</div>
                        <div className="text-xs text-gray-500">
                          Sconti: {item.discount_1}% / {item.discount_2}% / {item.discount_3}%
                          {item.advance_payment_discount > 0 && ` + Pag.Ant. ${item.advance_payment_discount}%`}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(item.base_price)}</td>
                      <td className="px-4 py-3 text-right font-medium text-blue-600">
                        {formatCurrency(item.price_with_excise_after_discounts)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Badge variant="outline" className="text-xs text-red-600">
                          -{formatCurrency(item.total_additional_costs)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-700 bg-green-50">
                        {formatCurrency(item.final_net_price)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {formatCurrency(item.industrial_cost)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={item.mol_euro >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {formatCurrency(item.mol_euro)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Badge variant={item.mol_percentage >= 20 ? 'default' : item.mol_percentage >= 10 ? 'secondary' : 'destructive'}>
                          {formatPercentage(item.mol_percentage)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Analysis;